import { useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { User, Mail, Shield, Clock, CheckCircle, XCircle, Eye, EyeOff, AlertCircle } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { PasswordRequirements } from '@/components/ui/password-requirements'
import { isPasswordValid, PASSWORD_REQUIREMENTS_MESSAGE } from '@/lib/password-rules'
import { useToast } from '@/hooks/use-toast'
import { userService } from '@/lib/api/userService'

// Zod schema for password change form
const passwordChangeSchema = z
  .object({
    currentPassword: z.string().min(1, 'Current password is required'),
    newPassword: z.string().refine(isPasswordValid, { message: PASSWORD_REQUIREMENTS_MESSAGE }),
    confirmPassword: z.string().min(1, 'Please confirm your new password'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  })

type PasswordChangeFormValues = z.infer<typeof passwordChangeSchema>

interface ProfileSettingModalProps {
  open: boolean
  onClose: () => void
}

export const ProfileSettingModal = ({ open, onClose }: ProfileSettingModalProps) => {
  const { toast } = useToast()

  // Password visibility state
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [passwordVerified, setPasswordVerified] = useState<boolean | null>(null)

  // Password change feedback state
  const [passwordChangeStatus, setPasswordChangeStatus] = useState<{
    type: 'success' | 'error' | null
    message: string
  }>({ type: null, message: '' })

  // React Hook Form setup
  const form = useForm<PasswordChangeFormValues>({
    resolver: zodResolver(passwordChangeSchema),
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
    mode: 'onChange',
  })

  // Fetch user profile using React Query
  const {
    data: userProfile,
    isLoading: isLoadingProfile,
    error: profileError,
  } = useQuery({
    queryKey: ['userProfile'],
    queryFn: () => userService.getCurrentUserProfile(),
    enabled: open,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: 1,
  })

  // Verify password mutation
  const verifyPasswordMutation = useMutation({
    mutationFn: (password: string) => userService.verifyPassword(password),
    onSuccess: (result) => {
      setPasswordVerified(result.verified)
      toast({
        title: result.verified ? 'Success' : 'Error',
        description: result.verified ? 'Password verified successfully!' : 'Current password is incorrect.',
        variant: result.verified ? 'default' : 'destructive',
      })
    },
    onError: () => {
      setPasswordVerified(false)
      toast({
        title: 'Error',
        description: 'Failed to verify password. Please try again.',
        variant: 'destructive',
      })
    },
  })

  const currentPasswordValue = form.watch('currentPassword')
  const newPasswordValue = form.watch('newPassword')

  // Change password mutation
  const changePasswordMutation = useMutation({
    mutationFn: ({ currentPassword, newPassword }: { currentPassword: string; newPassword: string }) =>
      userService.changePassword(currentPassword, newPassword),
    onSuccess: (result) => {
      console.log('Password change result:', result)

      if (result.success) {
        setPasswordChangeStatus({
          type: 'success',
          message: result.message || 'Password changed successfully!',
        })

        // Reset form
        form.reset()
        setPasswordVerified(null)

        // Auto-hide success message after 5 seconds
        setTimeout(() => {
          setPasswordChangeStatus({ type: null, message: '' })
        }, 5000)
      } else {
        setPasswordChangeStatus({
          type: 'error',
          message: result.message || 'Failed to change password.',
        })
      }
    },
    onError: (error) => {
      console.error('Password change error:', error)
      const errorMessage = error instanceof Error ? error.message : 'Failed to change password. Please try again.'
      setPasswordChangeStatus({
        type: 'error',
        message: errorMessage,
      })
    },
  })

  const handleVerifyPassword = () => {
    if (!currentPasswordValue) {
      toast({
        title: 'Validation Error',
        description: 'Please enter your current password.',
        variant: 'destructive',
      })
      return
    }

    setPasswordVerified(null)
    verifyPasswordMutation.mutate(currentPasswordValue)
  }

  const handleChangePassword = (values: PasswordChangeFormValues) => {
    // Clear any previous status
    setPasswordChangeStatus({ type: null, message: '' })

    changePasswordMutation.mutate({
      currentPassword: values.currentPassword,
      newPassword: values.newPassword,
    })
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-3">
              <User className="h-6 w-6" />
              {userProfile?.firstName && userProfile?.lastName
                ? `${userProfile.firstName} ${userProfile.lastName}`
                : 'My Profile'}
            </DialogTitle>
          </div>
        </DialogHeader>

        {isLoadingProfile ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-muted-foreground">Loading profile...</div>
          </div>
        ) : profileError ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-destructive">Failed to load profile. Please try again.</div>
          </div>
        ) : (
          <Tabs defaultValue="profile" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="profile">Profile</TabsTrigger>
              <TabsTrigger value="password">Change Password</TabsTrigger>
            </TabsList>

            <TabsContent value="profile" className="space-y-6">
              <div className="fanfair-card">
                <h3 className="text-lg font-semibold mb-4">Profile Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">First Name</Label>
                    <div className="font-medium">{userProfile?.firstName || 'N/A'}</div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Last Name</Label>
                    <div className="font-medium">{userProfile?.lastName || 'N/A'}</div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-muted-foreground flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      Email
                    </Label>
                    <div className="font-medium">{userProfile?.email || 'N/A'}</div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-muted-foreground flex items-center gap-2">
                      <Shield className="h-4 w-4" />
                      Role
                    </Label>
                    <div className="font-medium">{userProfile?.userRole || 'N/A'}</div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Entity Type</Label>
                    <div className="font-medium">{userProfile?.entityType || 'N/A'}</div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Entity ID</Label>
                    <div className="font-medium">{userProfile?.entityId || 'N/A'}</div>
                  </div>

                  <div className="space-y-2 col-span-2">
                    <Label className="text-muted-foreground flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      Last Sign In
                    </Label>
                    <div className="font-medium">{formatDate(userProfile?.lastSignInAt)}</div>
                  </div>
                </div>
              </div>

              <div className="fanfair-card">
                <h3 className="text-lg font-semibold mb-4">Security</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-muted-foreground">TOTP Status</Label>
                      <div className="mt-1">
                        <Badge variant={userProfile?.totpStatus === 'ENABLED' ? 'default' : 'secondary'}>
                          {userProfile?.totpStatus || 'NOT SET'}
                        </Badge>
                      </div>
                    </div>
                  </div>

                  {userProfile?.emailConfirmedAt && (
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">Email Confirmed</Label>
                      <div className="font-medium">{formatDate(userProfile.emailConfirmedAt)}</div>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="password" className="space-y-6">
              <div className="fanfair-card">
                <h3 className="text-lg font-semibold mb-4">Change Your Password</h3>

                {/* Success/Error Alert */}
                {passwordChangeStatus.type && (
                  <Alert
                    variant={passwordChangeStatus.type === 'success' ? 'default' : 'destructive'}
                    className="mb-4"
                  >
                    {passwordChangeStatus.type === 'success' ? (
                      <CheckCircle className="h-4 w-4" />
                    ) : (
                      <AlertCircle className="h-4 w-4" />
                    )}
                    <AlertDescription>{passwordChangeStatus.message}</AlertDescription>
                  </Alert>
                )}

                <Form {...form}>
                  <form onSubmit={form.handleSubmit(handleChangePassword)} className="space-y-4">
                    <FormField
                      control={form.control}
                      name="currentPassword"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Current Password *</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Input
                                type={showCurrentPassword ? 'text' : 'password'}
                                {...field}
                                onChange={(e) => {
                                  field.onChange(e)
                                  setPasswordVerified(null)
                                }}
                              />
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                              >
                                {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                              </Button>
                            </div>
                          </FormControl>
                          <div className="flex items-center gap-2 mt-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={handleVerifyPassword}
                              disabled={verifyPasswordMutation.isLoading || !currentPasswordValue}
                            >
                              {verifyPasswordMutation.isLoading ? 'Verifying...' : 'Verify Current Password'}
                            </Button>
                            {passwordVerified === true && <CheckCircle className="h-5 w-5 text-green-600" />}
                            {passwordVerified === false && <XCircle className="h-5 w-5 text-red-600" />}
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="newPassword"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>New Password *</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Input type={showNewPassword ? 'text' : 'password'} {...field} />
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                                onClick={() => setShowNewPassword(!showNewPassword)}
                              >
                                {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                              </Button>
                            </div>
                          </FormControl>
                          <PasswordRequirements password={newPasswordValue} className="mt-2" />
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="confirmPassword"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Confirm New Password *</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Input type={showConfirmPassword ? 'text' : 'password'} {...field} />
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              >
                                {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                              </Button>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="flex justify-end gap-3 pt-4">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          form.reset()
                          setPasswordVerified(null)
                        }}
                        disabled={changePasswordMutation.isLoading}
                      >
                        Clear
                      </Button>
                      <Button type="submit" disabled={changePasswordMutation.isLoading}>
                        {changePasswordMutation.isLoading ? 'Changing Password...' : 'Change Password'}
                      </Button>
                    </div>
                  </form>
                </Form>
              </div>
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  )
}
